/**
 * constants.ts — shared grid/tokens/actions/reward constants.
 *
 * Ported from `_proto_reflex_v1.py` (lines 66-120) verbatim. These define the
 * regime: a 15×15 grid, 8 directions of 1-cell movement, a 5×5 noiseless
 * window of opaque cell tokens, sparse immediate reward (no gamma, no
 * bootstrap — this is a contextual bandit, not an MDP).
 */

// ── Grid + tokens ──────────────────────────────────────────────────────
export const GRID = 15;
export const START_R = Math.floor(GRID / 2); // 7
export const START_C = Math.floor(GRID / 2); // 7
export const FREE = 0;
export const GOLD = 1;
export const STONE = 2;
export const N_TOKEN_VALUES = 3;

// ── Actions: 8 directions, 1-cell step ─────────────────────────────────
// Cardinals first, then diagonals — same order as mob's DIRECTIONS so the
// rendering's heading tick lines up across demos.
export const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [0, -1],
  [1, 0],
  [-1, 0],
  [-1, 1],
  [-1, -1],
  [1, 1],
  [1, -1],
];
export const N_ACTIONS = 8;

// ── Perception: raw 5×5 window around the agent (noiseless) ────────────
export const WINDOW = 5;
export const HALF_W = Math.floor(WINDOW / 2); // 2
export const WINDOW_FLAT = WINDOW * WINDOW; // 25

// ACTION-INVARIANT encoding (the load-bearing fix from the Python prototype,
// lines 82-93): each action's 75-dim one-hot window (25 cells × 3 token
// values) lives in the SHARED columns [:75] for every action, plus an 8-dim
// one-hot action ID in columns [75:83]. The destination token (cell 12 of
// the window) occupies the SAME feature index regardless of which action's
// window we're encoding, so the tree learns ONE rule "GOLD at destination ->
// reward" shared across all action slots — not eight disjoint per-slot rules
// that each get ~1/8 of the data. The earlier action-specific encoding
// (600 disjoint features) plateaued at 75% gold-grab; the invariant encoding
// hits ~100% within one world.
export const WINDOW_FEATURES = WINDOW_FLAT * N_TOKEN_VALUES; // 75
export const N_FEATURES = WINDOW_FEATURES + N_ACTIONS; // 75 + 8 = 83

// ── Reward (sparse, immediate — bandit, not MDP) ───────────────────────
export const R_GOLD = 1.0;
export const R_BUMP_DISCOVERED = -0.5; // bumping a barrier already discovered
export const R_EXPLORE = 0.0; // exploration is free

// ── Bandit hyperparameters (defaults — all live-tunable in the panel) ──
// TWO-TIMESCALE MEMORY: LONG carries token SEMANTICS across days (never
// reset); DAY carries the current day's SHAPE pattern (reset per day).
export const WINDOW_PER_DAY = 300; // short-term day buffer cap
export const LONG_CAP = 800; // long-term semantic buffer cap
export const N_TREES = 40;
export const COLD_START_K = 64; // ε-floor: uniform random until K observations
export const REFIT_EVERY = 40; // forest refit cadence (observations)

// ── Drift detector defaults (ECDD on prediction error) ─────────────────
export const DRIFT_LAM = 0.05; // EWMA decay
export const DRIFT_L_WARNING = 2.0; // ~95th percentile of N(0,1)
export const DRIFT_L_DRIFT = 3.0; // ~99.7th percentile
export const DRIFT_WARMUP = 50;
export const DRIFT_EXPLORE_WINDOW = 24; // forced-random actions after a drift

// The default tree min_samples_leaf — the prototype sweep (LEAF=10) found
// this the best within-day generalization/overfit tradeoff.
export const DEFAULT_MIN_SAMPLES_LEAF = 10;

// ── Day schedule (the discrete-drift evaluation) ───────────────────────
export const DAY_SCHEDULE = ['circles', 'squares', 'circles', 'blobs'] as const;
export const N_WORLDS_PER_DAY = 8;

// ── Gradual-drift schedule (the continuous-blend evaluation) ───────────
export const GRADUAL_SCHEDULE = ['circles', 'blobs'] as const;
export const N_GRADUAL_WORLDS = 32;

export type ShapeName = 'circles' | 'squares' | 'blobs' | 'spirals';
export type ArmMode = 'ts' | 'greedy' | 'random';
export type DriftStatus = 'warmup' | 'ok' | 'warning' | 'drift';
